import { ref, computed } from 'vue'
import api from '../api/axios'

interface Role {
  id: number
  name: string
  label: string
  created_at?: string
  updated_at?: string
}

interface Location {
  id: number
  airport_id: number
  rental_company_id: number
  is_locked: boolean
  location_type_id: number
  name: string
  address: string | null
  latitude: number | null
  longitude: number | null
  created_at: string
  updated_at: string
  location_type: {
    id: number
    name: string
    slug: string
    created_at: string
    updated_at: string
  }
}

interface Airport {
  id: number
  code: string
  name: string
  is_active: boolean
  is_locked: boolean
  created_at: string
  updated_at: string
  locations: Location[]
}

interface User {
  id: number
  name: string
  email: string
  email_verified_at: string | null
  role_id: number
  airport_id: number
  is_locked: number
  created_at: string
  updated_at: string
  role_name: string
  role: Role
  airport: Airport
}

interface AuthState {
  isAuthenticated: boolean
  user: User | null
  error: string | null
  selectedTerminal: number | null
  isInitialized: boolean
  driverVerified: boolean
  activeRole: string | null
  availableRoles: Role[]
}

// Token expiration configuration
const TOKEN_EXPIRATION_MINUTES = 1440 // 24 hours (matches backend sanctum.expiration)
const TOKEN_REFRESH_THRESHOLD_MINUTES = 60 // Refresh when less than 1 hour remains
const TOKEN_EXPIRATION_KEY = 'token_expires_at'
// The active role survives reload/offline so the UI knows which hat the user
// wears before /me responds. The token itself is the server-side source of truth.
const ACTIVE_ROLE_KEY = 'active_role'

// Global refresh state to prevent multiple simultaneous refresh attempts
let isRefreshing = false
let refreshPromise: Promise<boolean> | null = null
let refreshTimerId: ReturnType<typeof setTimeout> | null = null

interface SelectedLocation {
  id: number
  type: 'terminal' | 'servicecenter'
  name: string
}

// SECURITY: Global state stored in memory only, not localStorage
// This protects against XSS attacks that could steal tokens/user data
const state = ref<AuthState>({
  isAuthenticated: false,
  user: null,
  error: null,
  selectedTerminal: null,
  isInitialized: false,
  driverVerified: false,
  // Restore the active role immediately so role-gated routing works on a cold
  // start before /me resolves; /me then confirms it from the token.
  activeRole: localStorage.getItem(ACTIVE_ROLE_KEY),
  availableRoles: []
})

export function useAuth() {
  // Location selection (sessionStorage is acceptable for non-sensitive data)
  const getSelectedLocation = (): SelectedLocation | null => {
    const locationData = sessionStorage.getItem('selectedLocation')
    if (locationData) {
      try {
        return JSON.parse(locationData)
      } catch (e) {
        console.warn('Could not parse selectedLocation from sessionStorage')
      }
    }

    // Backward compatibility: check for old terminal selection
    const terminalId = sessionStorage.getItem('selectedTerminal')
    if (terminalId) {
      const locationObj = {
        id: parseInt(terminalId),
        type: 'terminal' as const,
        name: 'Terminal'
      }
      setSelectedLocation(locationObj)
      sessionStorage.removeItem('selectedTerminal')
      return locationObj
    }

    return null
  }

  const setSelectedLocation = (location: SelectedLocation) => {
    sessionStorage.setItem('selectedLocation', JSON.stringify(location))
    if (location.type === 'terminal') {
      sessionStorage.setItem('selectedTerminal', location.id.toString())
      state.value.selectedTerminal = location.id
    } else {
      sessionStorage.removeItem('selectedTerminal')
      state.value.selectedTerminal = null
    }
  }

  const clearSelectedLocation = () => {
    sessionStorage.removeItem('selectedLocation')
    sessionStorage.removeItem('selectedTerminal')
    state.value.selectedTerminal = null
  }

  const hasSelectedLocation = computed(() => {
    return getSelectedLocation() !== null
  })

  const selectedLocation = computed(() => {
    return getSelectedLocation()
  })

  // Legacy Terminal Functions (for backward compatibility)
  const getSelectedTerminal = () => {
    const location = getSelectedLocation()
    return location && location.type === 'terminal' ? location.id : null
  }

  const setSelectedTerminal = (terminalId: number, terminalName?: string) => {
    const location: SelectedLocation = {
      id: terminalId,
      type: 'terminal',
      name: terminalName || 'Terminal'
    }
    setSelectedLocation(location)
  }

  const clearSelectedTerminal = () => {
    clearSelectedLocation()
  }

  const hasSelectedTerminal = computed(() => {
    const location = getSelectedLocation()
    return location?.type === 'terminal'
  })

  const selectedTerminalId = computed(() => {
    return getSelectedTerminal()
  })

  // Service Center Functions
  const getSelectedServiceCenter = () => {
    const location = getSelectedLocation()
    return location && location.type === 'servicecenter' ? location.id : null
  }

  const setSelectedServiceCenter = (serviceCenterId: number, serviceCenterName: string) => {
    const location: SelectedLocation = {
      id: serviceCenterId,
      type: 'servicecenter',
      name: serviceCenterName
    }
    setSelectedLocation(location)
  }

  const hasSelectedServiceCenter = computed(() => {
    const location = getSelectedLocation()
    return location?.type === 'servicecenter'
  })

  // Request deduplication
  let fetchUserPromise: Promise<User | null> | null = null

  const fetchUser = async (): Promise<User | null> => {
    // If there's already a fetchUser call in progress, return the existing promise
    if (fetchUserPromise) {
      return fetchUserPromise
    }

    fetchUserPromise = (async () => {
      try {
        // Check if token exists before making API call
        const token = localStorage.getItem('token')
        if (!token) {
          state.value.user = null
          state.value.isAuthenticated = false
          return null
        }

        const response = await api.get('/api/auth/me')
        const { user, active_role, available_roles, token: newToken, token_rotated } = response.data

        // SECURITY: Handle automatic token rotation from server
        if (token_rotated && newToken) {
          storeTokenWithExpiration(newToken)
          console.info('[Auth] Token rotated by server')
        }

        if (user) {
          state.value.user = user
          // /me derives active_role from the token — authoritative. Keep the
          // persisted value if the server didn't send one (older backend).
          if (active_role) {
            setActiveRole(active_role)
          }
          state.value.availableRoles = available_roles ?? (user.role ? [user.role] : [])
          state.value.isAuthenticated = true
        } else {
          state.value.user = null
          state.value.isAuthenticated = false
          state.value.driverVerified = false
        }

        return user
      } catch (err: any) {
        // Handle server errors gracefully - don't clear auth on temporary errors
        if (err?.response?.status === 503 || err?.response?.status === 502 || err?.response?.status === 504) {
          console.warn('fetchUser - Server temporarily unavailable, keeping auth state')
          return state.value.user
        }

        // Handle offline mode
        if (!navigator.onLine) {
          console.warn('fetchUser - Offline, keeping existing auth state')
          return state.value.user
        }

        // Clear auth on explicit authentication errors (401/403)
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          state.value.user = null
          state.value.isAuthenticated = false
          state.value.driverVerified = false
          localStorage.removeItem('token')
        }

        return null
      } finally {
        fetchUserPromise = null
      }
    })()

    return fetchUserPromise
  }

  // Initialize auth status on app start
  const initializeAuth = async () => {
    if (state.value.isInitialized) {
      return
    }

    try {
      // Check if token exists and is not expired
      const token = localStorage.getItem('token')
      if (token) {
        // If token is expired, try to refresh before fetching user
        if (isTokenExpired()) {
          console.log('[Auth] Token expired, attempting refresh before init...')
          const refreshed = await refreshToken()
          if (!refreshed) {
            console.log('[Auth] Refresh failed, clearing auth')
            clearAuthState()
            state.value.isInitialized = true
            return
          }
        }

        await fetchUser()

        // Setup refresh timer if authenticated
        if (state.value.isAuthenticated) {
          setupTokenRefreshTimer()

          // Restore driver verification status from localStorage
          if (localStorage.getItem('driverVerified') === 'true') {
            state.value.driverVerified = true
          }
        }
      }
    } catch (err) {
      // Silent fail - user is not authenticated
      console.error('[Auth] Init error:', err)
    } finally {
      state.value.isInitialized = true
      // Clean up user data from localStorage (only token is stored now)
      localStorage.removeItem('user')
    }
  }

  // The active role drives all role gating. It comes from the role-scoped token
  // (set by login/switch-role/me); we fall back to the user's primary role for
  // legacy sessions or before /me has resolved.
  const userRole = computed(() => {
    return state.value.activeRole
      || state.value.user?.role_name
      || (state.value.user?.role_id ? `role_${state.value.user.role_id}` : null)
  })

  const activeRole = computed(() => state.value.activeRole)
  const availableRoles = computed(() => state.value.availableRoles)
  const hasMultipleRoles = computed(() => state.value.availableRoles.length > 1)

  // Persist the active role alongside the token so a reload restores it.
  const setActiveRole = (role: string | null) => {
    state.value.activeRole = role
    if (role) {
      localStorage.setItem(ACTIVE_ROLE_KEY, role)
    } else {
      localStorage.removeItem(ACTIVE_ROLE_KEY)
    }
  }

  // Clear state that only makes sense for the previous active role, so a switch
  // re-runs the per-role onboarding (terminal/location selection).
  // NOTE: driverVerified is intentionally NOT reset here — the Führerschein check
  // is per-login (tied to the person, not the active role), so switching back to
  // Fahrer within a login must not re-prompt. It's reset by login() and logout().
  const resetRoleScopedState = () => {
    state.value.selectedTerminal = null
    sessionStorage.removeItem('selectedTerminal')
    sessionStorage.removeItem('selectedLocation')
  }

  const isChecker = computed(() => {
    const role = userRole.value
    return role === 'checker' || role === 'role_2'
  })

  const isDriver = computed(() => {
    const role = userRole.value
    return role === 'driver' || role === 'role_3'
  })

  const isAdmin = computed(() => {
    const role = userRole.value
    return role === 'admin' || role === 'role_1'
  })

  const isSchichtleiter = computed(() => {
    const role = userRole.value
    return role === 'schichtleiter' || role === 'role_5'
  })

  const isDisponent = computed(() => {
    const role = userRole.value
    return role === 'disponent' || role === 'dispatcher' || role === 'role_4'
  })

  // Token expiration helpers
  const storeTokenWithExpiration = (token: string, expiresInMinutes: number = TOKEN_EXPIRATION_MINUTES) => {
    localStorage.setItem('token', token)
    const expiresAt = Date.now() + (expiresInMinutes * 60 * 1000)
    localStorage.setItem(TOKEN_EXPIRATION_KEY, expiresAt.toString())
    console.log('[Auth] Token stored, expires at:', new Date(expiresAt).toISOString())

    // Setup refresh timer
    setupTokenRefreshTimer()
  }

  const getTokenExpirationTime = (): number | null => {
    const expiresAt = localStorage.getItem(TOKEN_EXPIRATION_KEY)
    return expiresAt ? parseInt(expiresAt, 10) : null
  }

  const isTokenExpiringSoon = (thresholdMinutes: number = TOKEN_REFRESH_THRESHOLD_MINUTES): boolean => {
    const expiresAt = getTokenExpirationTime()
    if (!expiresAt) return true // If no expiration stored, assume expired

    const now = Date.now()
    const thresholdMs = thresholdMinutes * 60 * 1000
    return (expiresAt - now) < thresholdMs
  }

  const isTokenExpired = (): boolean => {
    const expiresAt = getTokenExpirationTime()
    if (!expiresAt) return true
    return Date.now() >= expiresAt
  }

  const getTokenRemainingMinutes = (): number => {
    const expiresAt = getTokenExpirationTime()
    if (!expiresAt) return 0
    const remaining = expiresAt - Date.now()
    return Math.max(0, Math.floor(remaining / (60 * 1000)))
  }

  // Token refresh function
  const refreshToken = async (): Promise<boolean> => {
    // If already refreshing, return the existing promise
    if (isRefreshing && refreshPromise) {
      console.log('[Auth] Refresh already in progress, waiting...')
      return refreshPromise
    }

    // Check if we have a token to refresh
    const currentToken = localStorage.getItem('token')
    if (!currentToken) {
      console.log('[Auth] No token to refresh')
      return false
    }

    isRefreshing = true
    refreshPromise = (async () => {
      try {
        console.log('[Auth] Refreshing token...')
        const response = await api.post('/api/auth/refresh')
        const { token: newToken, active_role } = response.data

        if (newToken) {
          storeTokenWithExpiration(newToken)
          // The refreshed token keeps the same role; sync if the server says so.
          if (active_role) {
            setActiveRole(active_role)
          }
          console.log('[Auth] Token refreshed successfully')
          return true
        }

        console.warn('[Auth] Refresh response did not contain new token')
        return false
      } catch (err: any) {
        console.error('[Auth] Token refresh failed:', err?.response?.status || err.message)

        // If refresh fails with 401, the session is truly expired
        if (err?.response?.status === 401) {
          clearAuthState()
        }
        return false
      } finally {
        isRefreshing = false
        refreshPromise = null
      }
    })()

    return refreshPromise
  }

  // Setup proactive token refresh timer
  const setupTokenRefreshTimer = () => {
    // Clear any existing timer
    if (refreshTimerId) {
      clearTimeout(refreshTimerId)
      refreshTimerId = null
    }

    const expiresAt = getTokenExpirationTime()
    if (!expiresAt) return

    const now = Date.now()
    // Refresh at 80% of token lifetime (e.g., after ~19 hours for a 24-hour token)
    const refreshAt = expiresAt - (TOKEN_REFRESH_THRESHOLD_MINUTES * 60 * 1000)
    const delay = refreshAt - now

    if (delay > 0) {
      console.log(`[Auth] Token refresh scheduled in ${Math.round(delay / 60000)} minutes`)
      refreshTimerId = setTimeout(async () => {
        console.log('[Auth] Proactive token refresh triggered')
        const success = await refreshToken()
        if (!success) {
          console.warn('[Auth] Proactive refresh failed, user may need to re-login soon')
        }
      }, delay)
    } else if (!isTokenExpired()) {
      // Token is expiring soon but not yet expired, refresh now
      console.log('[Auth] Token expiring soon, refreshing now')
      refreshToken()
    }
  }

  // Clear all auth state
  const clearAuthState = () => {
    state.value.user = null
    state.value.isAuthenticated = false
    state.value.driverVerified = false
    state.value.availableRoles = []
    setActiveRole(null)
    localStorage.removeItem('token')
    localStorage.removeItem(TOKEN_EXPIRATION_KEY)
    localStorage.removeItem('driverVerified')
    if (refreshTimerId) {
      clearTimeout(refreshTimerId)
      refreshTimerId = null
    }
  }

  /**
   * Switch the active role (strict per-role token). Re-mints the session token
   * scoped to the target role and resets role-scoped state so the per-role
   * onboarding re-runs. The caller is responsible for routing to the new
   * landing and for guarding against offline/pending-queue (DEV-80/81).
   *
   * @returns the new active role on success, or null on failure.
   */
  const switchRole = async (roleName: string): Promise<string | null> => {
    try {
      const response = await api.post('/api/auth/switch-role', { role: roleName })
      const { token, active_role, available_roles } = response.data

      if (token) {
        storeTokenWithExpiration(token)
      }
      setActiveRole(active_role ?? roleName)
      if (available_roles) {
        state.value.availableRoles = available_roles
      }
      resetRoleScopedState()

      return state.value.activeRole
    } catch (err: any) {
      console.error('[Auth] Role switch failed:', err?.response?.status || err.message)
      return null
    }
  }

  const login = async (email: string, password: string) => {
    try {
      state.value.error = null
      state.value.driverVerified = false

      const response = await api.post('/api/auth/login', { email, password })
      const { token, user, active_role, available_roles } = response.data

      // Store token with expiration tracking
      if (token) {
        storeTokenWithExpiration(token)
      }

      state.value.user = user
      setActiveRole(active_role ?? user?.role?.name ?? null)
      state.value.availableRoles = available_roles ?? (user?.role ? [user.role] : [])
      state.value.isAuthenticated = true

      return user
    } catch (err: any) {
      console.error('Login error:', err)
      // Extract error message from server response
      if (err?.response?.data?.errors?.email?.[0]) {
        state.value.error = err.response.data.errors.email[0]
      } else if (err?.response?.data?.message) {
        state.value.error = err.response.data.message
      } else {
        state.value.error = 'Ungültige Anmeldedaten'
      }
      throw err
    }
  }

  const logout = async () => {
    try {
      // Call logout endpoint to invalidate token on server
      await api.post('/api/auth/logout')
    } catch (err) {
      console.error('Logout error:', err)
    } finally {
      // Clear all auth state including token expiration
      clearAuthState()
      state.value.selectedTerminal = null

      // Clear session storage
      sessionStorage.removeItem('selectedTerminal')
      sessionStorage.removeItem('selectedLocation')
    }
  }

  // Driver verification - stored in memory only
  const isDriverVerified = (): boolean => {
    if (!state.value.isAuthenticated || !state.value.user) {
      return false
    }

    if (!isDriver.value) {
      return false
    }

    return state.value.driverVerified
  }

  const markDriverAsVerified = () => {
    if (!state.value.isAuthenticated || !state.value.user) {
      console.warn('Cannot mark driver as verified: not authenticated')
      return false
    }

    if (!isDriver.value) {
      console.warn('Cannot mark driver as verified: user is not a driver')
      return false
    }

    state.value.driverVerified = true
    localStorage.setItem('driverVerified', 'true')
    return true
  }

  /**
   * Where to send the user for their current active role. Shared by the login
   * redirect and the role-selection screen so both land consistently after a
   * switch re-runs the per-role onboarding.
   */
  const roleLandingPath = (): string => {
    if (isDriver.value && !isDriverVerified()) return '/driver-verification'
    if (isChecker.value && !hasSelectedTerminal.value) return '/terminal-selection'
    if (isDisponent.value && !hasSelectedLocation.value) return '/terminal-selection'
    return '/dashboard'
  }

  return {
    state,
    userRole,
    activeRole,
    availableRoles,
    hasMultipleRoles,
    switchRole,
    roleLandingPath,
    isChecker,
    isDriver,
    isAdmin,
    isSchichtleiter,
    isDisponent,
    login,
    logout,
    fetchUser,
    initializeAuth,
    isDriverVerified,
    markDriverAsVerified,
    // Token management
    refreshToken,
    isTokenExpiringSoon,
    isTokenExpired,
    getTokenRemainingMinutes,
    setupTokenRefreshTimer,
    clearAuthState,
    // Enhanced Location Functions
    getSelectedLocation,
    setSelectedLocation,
    clearSelectedLocation,
    hasSelectedLocation,
    selectedLocation,
    // Terminal Functions (backward compatibility)
    getSelectedTerminal,
    setSelectedTerminal,
    clearSelectedTerminal,
    hasSelectedTerminal,
    selectedTerminalId,
    // Service Center Functions
    getSelectedServiceCenter,
    setSelectedServiceCenter,
    hasSelectedServiceCenter
  }
}
